import "@/styles/globals.css";
import type { AppProps } from "next/app";
import Footer from "../components/footer";
import Header from "../components/header";
import { PageErrorBoundary } from "../components/error-boundary";
import { Fredoka } from "next/font/google";
import dynamic from "next/dynamic";
import { Analytics } from "@vercel/analytics/next";
import { useTelemetry, usePerformanceTelemetry, debugTelemetry } from "../lib/telemetry";
import { useEffect } from "react";

// Named imports using dynamic import
const GoogleAnalytics = dynamic(
  () => import("@next/third-parties/google").then((mod) => mod.GoogleAnalytics),
  { ssr: false },
);



const fredoka = Fredoka({
  weight: "400",
  subsets: ["latin"],
});

// Telemetry wrapper component
function TelemetryProvider({ children }: { children: React.ReactNode }) {
  const telemetry = useTelemetry();

  // Initialize performance monitoring
  usePerformanceTelemetry();

  useEffect(() => {
    // Enable debug mode in development
    if (process.env.NODE_ENV === 'development') {
      debugTelemetry.enableDebugMode();
      debugTelemetry.testSetup();
    }

    // Global error handlers to catch unhandled errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Log error details for debugging
      console.error('[Error Handler] Unhandled promise rejection:', {
        timestamp: new Date().toISOString(),
        errorType: 'unhandled_promise_rejection',
        source: 'global_handler',
        promise: event.promise,
        reason: event.reason,
      });

      // Track with telemetry
      telemetry.track({
        name: 'unhandled_error',
        properties: {
          type: 'promise_rejection',
          error: String(event.reason),
          timestamp: new Date().toISOString(),
        },
        tags: {
          error_type: 'unhandled_promise_rejection',
        },
      });
    };

    const handleError = (event: ErrorEvent) => {
      // Filter out webkit-related errors that are false positives
      const errorMessage = event.message?.toLowerCase() || '';
      if (errorMessage.includes('webkit.messagehandlers') || 
          errorMessage.includes('webkit') && errorMessage.includes('undefined')) {
        console.debug('Filtered webkit error (likely from iOS/mobile browser environment):', event.message);
        return; // Don't report these to Sentry as they're environmental
      }

      console.error('Global error:', event.error);

      // Log error details for debugging
      console.error('[Error Handler] Global error:', {
        timestamp: new Date().toISOString(),
        errorType: 'global_error',
        source: 'global_handler',
        error: event.error,
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });

      // Track with telemetry
      telemetry.track({
        name: 'unhandled_error',
        properties: {
          type: 'global_error',
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString(),
        },
        tags: {
          error_type: 'global_error',
        },
      });
    };

    // Add global error listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Set application context
    telemetry.setContext('app', {
      name: 'Graceful Journey Learning Center',
      version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
      buildTime: process.env.NEXT_PUBLIC_BUILD_TIME,
      buildId: process.env.NEXT_PUBLIC_BUILD_ID,
      environment: process.env.NODE_ENV,
    });

    // Set initial tags
    telemetry.setTags({
      app: 'graceful-journey',
      component: 'main_app',
    });

    // Track app initialization
    telemetry.track({
      name: 'app_initialized',
      properties: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      tags: {
        session_type: 'app_start',
      },
    });

    // Track page visibility changes
    const handleVisibilityChange = () => {
      telemetry.track({
        name: 'page_visibility_change',
        properties: {
          hidden: document.hidden,
          visibilityState: document.visibilityState,
          timestamp: new Date().toISOString(),
        },
        tags: {
          visibility: document.hidden ? 'hidden' : 'visible',
        },
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track connection changes
    const handleOnline = () => {
      telemetry.track({
        name: 'connection_change',
        properties: {
          online: true,
          timestamp: new Date().toISOString(),
        },
        tags: {
          connection_status: 'online',
        },
      });
    };

    const handleOffline = () => {
      telemetry.track({
        name: 'connection_change',
        properties: {
          online: false,
          timestamp: new Date().toISOString(),
        },
        tags: {
          connection_status: 'offline',
        },
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [telemetry]);

  return <>{children}</>;
}

export default function App({ Component, pageProps }: AppProps) {
  return (
    <PageErrorBoundary context={{ component: 'App', route: 'unknown' }}>
      <TelemetryProvider>
        <main className={fredoka.className}>
          <Header />
          <GoogleAnalytics gaId="G-PPDVFWY4KX" />
          <Analytics />
          <Component {...pageProps} />
          <Footer />
        </main>
      </TelemetryProvider>
    </PageErrorBoundary>
  );
}
